/**
 * Opening bid. $13MM is Eagle's published ask only.
 * Every other deal opens at the stated ask on file, then the seller-case
 * PDP PV10, and only then validation.refPdpPv10MM.
 */

export const EAGLE_DEAL_ID = 'eagle-etx-2026';
export const EAGLE_ASK_MM = 13;
export const SELLER_ASK_BID_LABEL = 'default bid = seller ask';
export const SELLER_PV10_BID_LABEL = 'default bid = seller PV10';

function finite(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function positive(v) {
  const n = finite(v);
  return n != null && n > 0 ? n : null;
}

function round3(n) {
  return Math.round(n * 1000) / 1000;
}

/**
 * Stated ask in $MM. A bare $13 on any deal other than Eagle is the old
 * unscoped default, not that deal's ask.
 */
export function statedAskMM(pkg, explicit = null, { dealId = null } = {}) {
  const id = dealId || pkg?.meta?.dealId || null;
  const candidates = [
    explicit,
    pkg?.sellerAskMM,
    pkg?.meta?.sellerAskMM,
    pkg?.meta?.askMM,
    pkg?.meta?.askingPriceMM,
    pkg?.assumptions?.sellerAskMM,
    pkg?.assumptions?.askMM,
    pkg?.validation?.sellerAskMM,
    pkg?.validation?.askMM,
    pkg?.assumptions?.bidPriceMM,
  ];
  for (const c of candidates) {
    const n = positive(c);
    if (n == null) continue;
    if (id !== EAGLE_DEAL_ID && n === EAGLE_ASK_MM) continue;
    return n;
  }
  return null;
}

/** Seller-case PDP PV10 ($MM). The oneline hint, not the reference sum. */
export function sellerCasePdpPv10MM(pkg) {
  if (!pkg) return null;
  const mm = positive(pkg.meta?.onelinePv10MMHint)
    ?? positive(pkg.validation?.onelinePv10MMHint)
    ?? positive(pkg.meta?.sellerPdpPv10MM)
    ?? positive(pkg.validation?.sellerPdpPv10MM);
  if (mm != null) return mm;
  const thousands = positive(pkg.meta?.onelinePv10MHint ?? pkg.validation?.onelinePv10MHint);
  return thousands != null ? thousands / 1000 : null;
}

function assetRefPdpPv10MM(pkg) {
  const assets = (pkg?.assets || []).filter((a) => !a.resCat || a.resCat === '1PDP');
  const sum = assets.reduce((s, a) => s + (finite(a.ref?.pv10) || 0), 0);
  if (!(sum > 0)) return null;
  // A single well above $1,000 is dollars. A figure already in the millions is tiny.
  return sum > 1000 ? sum / 1e6 : sum;
}

/**
 * Seller PDP PV10 in $MM. Seller-case oneline first; refPdpPv10MM last.
 * ref.pv10 on smoke fixtures is dollars, not millions.
 */
export function sellerPv10MM(pkg) {
  if (!pkg) return null;
  const sellerCase = sellerCasePdpPv10MM(pkg);
  if (sellerCase != null) return sellerCase;
  const ref = positive(pkg.validation?.refPdpPv10MM);
  if (ref != null) return ref;
  return assetRefPdpPv10MM(pkg);
}

/**
 * Copy a published ask onto the package when the package does not already
 * carry one. Does not copy Eagle's $13 onto another deal.
 */
export function applyStatedAsk(pkg, sellerAskMM) {
  if (!pkg || typeof pkg !== 'object') return pkg;
  const dealId = pkg.meta?.dealId || null;
  if (statedAskMM(pkg, null, { dealId }) != null) return pkg;
  const ask = positive(sellerAskMM);
  if (ask == null) return pkg;
  if (dealId !== EAGLE_DEAL_ID && ask === EAGLE_ASK_MM) return pkg;
  return { ...pkg, meta: { ...(pkg.meta || {}), sellerAskMM: ask } };
}

/**
 * Range max for the Decision bid slider, in $MM.
 * 20 covers Eagle. A larger opening bid steps up by $5 so the thumb can sit on it.
 */
export function bidSliderMaxMM(bid) {
  const n = positive(bid) || 0;
  return Math.max(20, Math.ceil(n / 5) * 5);
}

/**
 * Ask written onto decision.json, and onto the package when it has none.
 * A stated ask on the package, then a stated ask on the VDR, then (oil only)
 * the seller-case PDP PV10, then the live model rounded to $0.1MM.
 * A PV10 hint never becomes the ask while a different stated ask is on file.
 * Eagle stays $13MM. refPdpPv10MM is never published as an ask.
 */
export function publishedSellerAskMM({
  dealId = null,
  meta = null,
  pkg = null,
  livePv10MM = null,
  oil = false,
} = {}) {
  if (dealId === EAGLE_DEAL_ID) return EAGLE_ASK_MM;

  const packageAsk = statedAskMM(pkg, null, { dealId });
  if (packageAsk != null) return packageAsk;

  const vdrAsk = statedAskMM(
    meta ? { meta: { ...meta, dealId: meta.dealId || dealId } } : null,
    null,
    { dealId },
  );
  if (vdrAsk != null) return vdrAsk;

  if (!oil) return null;

  const sellerCase = sellerCasePdpPv10MM(pkg)
    ?? sellerCasePdpPv10MM(meta ? { meta } : null);
  if (sellerCase != null) return sellerCase;

  const live = positive(livePv10MM);
  return live != null ? +live.toFixed(1) : null;
}

/**
 * @returns {{ bidPriceMM: number|null, label: string|null }}
 * label names the source: seller ask, or seller PV10.
 */
export function defaultBidForDeal(dealId, pkg, { sellerAskMM = null, storedBid = null, bidEdited = false } = {}) {
  const stored = finite(storedBid);
  if (bidEdited && stored != null) return { bidPriceMM: stored, label: null };

  if (dealId === EAGLE_DEAL_ID) {
    if (stored != null) return { bidPriceMM: stored, label: null };
    const ask = finite(sellerAskMM);
    return { bidPriceMM: ask != null ? ask : EAGLE_ASK_MM, label: null };
  }

  // A stored bid other than the unscoped $13MM Eagle default is the user's number.
  if (stored != null && stored !== EAGLE_ASK_MM) return { bidPriceMM: stored, label: null };

  const ask = statedAskMM(pkg, sellerAskMM, { dealId });
  if (ask != null) return { bidPriceMM: ask, label: SELLER_ASK_BID_LABEL };

  const pv = sellerPv10MM(pkg);
  if (pv != null) return { bidPriceMM: round3(pv), label: SELLER_PV10_BID_LABEL };
  return { bidPriceMM: stored, label: null };
}
